import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaUserTie } from "react-icons/fa6";
import { Camera, Loader2 } from "lucide-react";
import { Button } from "../../misc/Button";
import CustomToast from "../../misc/CustomToast";
import CorporateHeadshotCropModal from "../../settings/CorporateHeadshotCropModal";
import { cn } from "../../../utils/classMerger";
import { getAuthHeader } from "../../../utils/authHeader";
import {
    CORPORATE_HEADSHOT_MIN_DIMENSION,
    getImageDimensions,
} from "../../../utils/imageValidation";
import { clearImage } from "../../../utils/dexieImageCache";
import useAppSelector from "../../../hooks/useAppSelector";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { setUser } from "../../../store/authSlice";
import { User } from "../../../types/user";

interface CorporateHeadshotFieldProps {
    className?: string;
}

// The backend response doesn't carry Firebase Auth fields the UI relies on;
// re-attach them from the current user so dispatch(setUser) doesn't drop them.
const mergeFirebaseFields = (
    updatedUser: User,
    currentUser: User | null
): User => ({
    ...updatedUser,
    photoURL: currentUser?.photoURL,
    providerId: currentUser?.providerId ?? "",
    uid: currentUser?.uid ?? "",
    displayName: currentUser?.displayName ?? null,
    emailVerified: currentUser?.emailVerified ?? false,
});

const MIME_TO_EXT: Record<string, string[]> = {
    "image/jpeg": ["jpg", "jpeg"],
    "image/png": ["png"],
    "image/webp": ["webp"],
};

// The slideshow uses the user's Corporate Headshot as the "person" in the
// overlay. Upload/replace it inline (validate → crop → upload), so the user
// never leaves the slideshow flow.
const CorporateHeadshotField: React.FC<CorporateHeadshotFieldProps> = ({
    className = "",
}) => {
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.auth.user);
    const headshot = user?.corporateHeadshot;

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [pendingCropFile, setPendingCropFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    const openPicker = (): void => fileInputRef.current?.click();

    const handleSelectFile = async (
        e: React.ChangeEvent<HTMLInputElement>
    ): Promise<void> => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;

        if (!Object.keys(MIME_TO_EXT).includes(file.type)) {
            toast.error(
                <CustomToast
                    message="Only JPEG, PNG, and WebP images are supported"
                    type="error"
                />
            );
            return;
        }
        if (file.size === 0) {
            toast.error(<CustomToast message="File is empty" type="error" />);
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error(
                <CustomToast
                    message="Image must be less than 5 MB"
                    type="error"
                />
            );
            return;
        }
        const ext = file.name.split(".").pop()?.toLowerCase();
        if (!ext || !MIME_TO_EXT[file.type]?.includes(ext)) {
            toast.error(
                <CustomToast
                    message="File extension does not match image type"
                    type="error"
                />
            );
            return;
        }

        try {
            const { width, height } = await getImageDimensions(file);
            if (
                width < CORPORATE_HEADSHOT_MIN_DIMENSION ||
                height < CORPORATE_HEADSHOT_MIN_DIMENSION
            ) {
                toast.error(
                    <CustomToast
                        message="Please upload a higher-resolution photo so it looks sharp in your generated videos."
                        type="error"
                    />
                );
                return;
            }
        } catch {
            toast.error(
                <CustomToast
                    message="Could not read image. Try a different file."
                    type="error"
                />
            );
            return;
        }

        // Hand off to the crop modal; upload happens on confirm.
        setPendingCropFile(file);
    };

    const handleCropConfirm = async (blob: Blob): Promise<void> => {
        setPendingCropFile(null);
        setUploading(true);
        const cacheKey = user?._id
            ? `corporateHeadshot-${user._id}`
            : undefined;

        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const {
                data: { signedUrl, key },
            } = await axios.post(
                `${backendUrl}/settings/corporate-headshot/signed-url`,
                {
                    contentType: "image/jpeg",
                    extension: "jpg",
                    fileSize: blob.size,
                },
                { headers }
            );

            await axios.put(signedUrl, blob, {
                headers: { "Content-Type": "image/jpeg" },
            });

            const { data: updatedUser } = await axios.post(
                `${backendUrl}/settings/corporate-headshot`,
                { key },
                { headers }
            );

            if (cacheKey) {
                try {
                    await clearImage(cacheKey);
                } catch {
                    // Cache miss is harmless.
                }
            }

            dispatch(setUser(mergeFirebaseFields(updatedUser, user)));
            toast.success(
                <CustomToast
                    message="Corporate headshot uploaded."
                    type="success"
                />
            );
        } catch {
            toast.error(
                <CustomToast
                    message="Failed to upload corporate headshot."
                    type="error"
                />
            );
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className={cn("space-y-2", className)}>
            <h4 className="text-base font-semibold text-primary">
                Corporate Headshot
            </h4>

            {/* One layout for both states: avatar tile (headshot or
                placeholder) + matching copy + action button. Stacks on phones
                (avatar+copy row, then a full-width button) and sits inline from
                480px up. Only `sxl` is a named breakpoint here, so the
                responsive rules use arbitrary variants. */}
            <div className="flex flex-col gap-3 rounded-md border border-gray-200 p-3 min-[480px]:flex-row min-[480px]:items-center min-[480px]:gap-4">
                <div className="flex min-w-0 items-center gap-4 min-[480px]:flex-1">
                    <button
                        type="button"
                        onClick={openPicker}
                        disabled={uploading}
                        aria-label={
                            headshot
                                ? "Change corporate headshot"
                                : "Add corporate headshot"
                        }
                        className="group relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-100"
                    >
                        {headshot ? (
                            <img
                                src={headshot}
                                alt="Corporate headshot"
                                className="h-full w-full object-cover"
                            />
                        ) : (
                            <span className="flex h-full w-full items-center justify-center">
                                <FaUserTie className="h-6 w-6 text-neutral-400" />
                            </span>
                        )}
                        <span
                            className={cn(
                                "absolute inset-0 flex items-center justify-center bg-black/40 text-white transition-opacity",
                                uploading
                                    ? "opacity-100"
                                    : "opacity-0 group-hover:opacity-100"
                            )}
                        >
                            {uploading ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                                <Camera className="h-5 w-5" />
                            )}
                        </span>
                    </button>

                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-primary">
                            {headshot
                                ? "Corporate headshot added"
                                : "Add your corporate headshot"}
                        </p>
                        <p className="text-xs text-neutral-500">
                            Used as the person shown in your slideshow.
                        </p>
                    </div>
                </div>

                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={openPicker}
                    disabled={uploading}
                    className="w-full flex-shrink-0 whitespace-nowrap min-[480px]:w-auto"
                >
                    {uploading ? "Uploading…" : headshot ? "Change" : "Add"}
                </Button>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleSelectFile}
            />

            {pendingCropFile && (
                <CorporateHeadshotCropModal
                    file={pendingCropFile}
                    onConfirm={handleCropConfirm}
                    onCancel={() => setPendingCropFile(null)}
                />
            )}
        </div>
    );
};

export default CorporateHeadshotField;
